import * as Sentry from '@sentry/remix';
import type { SportsbookMonitor, SportsbookMonitorContext } from '@copybet/bet';

type SentryLevel = 'error' | 'info' | 'warning';

const mapLevel = (level?: SportsbookMonitorContext['level']): SentryLevel => {
  if (level === 'warning') {
    return 'warning';
  }

  if (level === 'info') {
    return 'info';
  }

  return 'error';
};

export const normalizeError = (error: unknown, source: string): Error => {
  if (error instanceof Error) {
    return error;
  }

  if (typeof error === 'string') {
    return new Error(error);
  }

  // DOM Events JSON.stringify to '{"isTrusted":true}' — name the event type instead.
  if (typeof Event !== 'undefined' && error instanceof Event) {
    return new Error(`${source}: ${error.type} event`);
  }

  try {
    return new Error(`${source}: ${JSON.stringify(error)}`);
  } catch {
    return new Error(`${source}: ${String(error)}`);
  }
};

const applySportsbookScope = (scope: Sentry.Scope, context: SportsbookMonitorContext): void => {
  scope.setLevel(mapLevel(context.level));
  scope.setTag('feature', 'sportsbook');
  scope.setTag('sportsbook_source', context.source);

  if (context.details) {
    scope.setContext('sportsbook', context.details);
  }
};

export const sportsbookSentryMonitor: SportsbookMonitor = {
  captureException(error, context) {
    Sentry.withScope((scope) => {
      applySportsbookScope(scope, context);
      Sentry.captureException(normalizeError(error, context.source));
    });
  },
  captureMessage(message, context) {
    Sentry.withScope((scope) => {
      applySportsbookScope(scope, context);
      Sentry.captureMessage(message);
    });
  },
};
